import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import api, { formatApiError } from "@/lib/api";
import { fmtNum } from "@/utils/toEnglishDigits";
import { fmtDate, fmtTime } from "@/utils/date";
import {
  Bell,
  Gear,
  ClockCountdown,
  User as UserIcon,
  Info,
  PaperPlaneTilt,
  Alarm,
  UsersThree,
} from "@phosphor-icons/react";

// Human-friendly "منذ X"
function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return "منذ الآن";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `منذ ${fmtNum(minutes)} دقيقة`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `منذ ${fmtNum(hours)} ساعة`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `منذ ${fmtNum(days)} يوم`;
  return `${fmtDate(iso)}`;
}

const TYPE_LABEL = {
  driver_assigned: "تعيين سائق",
  booking_status: "تحديث حالة الحجز",
  new_booking: "حجز جديد",
  company_registered: "تسجيل شركة",
  driver_registered: "تسجيل سائق",
  test: "إشعار تجريبي",
};

function typeLabel(t) {
  return TYPE_LABEL[t] || (t || "إشعار");
}

// Toggle switch — off = gold translucent (thumb on the right in RTL),
// on = green (thumb translates to the visual "left").
function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? "bg-[#10B981]" : "bg-primary/30"
      }`}
    >
      <span
        className={`absolute top-[2px] h-5 w-5 rounded-full transition-transform ${
          checked ? "translate-x-[-2px] bg-white left-0" : "translate-x-[-24px] bg-primary left-full"
        }`}
        style={{ transitionProperty: "transform, background-color" }}
      />
    </button>
  );
}

function SettingRow({ label, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3 border-b border-primary/10 last:border-0">
      <span className="text-white text-sm font-semibold text-right flex-1">{label}</span>
      <Toggle checked={!!checked} onChange={onChange} testId={testId} />
    </div>
  );
}

function SectionCard({ title, icon: Icon, children, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl border border-primary/25 bg-card p-4">
      <div className="flex items-center gap-1.5 pb-2.5 mb-3 border-b border-primary/15">
        {Icon && <Icon size={16} weight="duotone" className="text-primary" />}
        <span className="text-primary font-bold text-sm">{title}</span>
      </div>
      {children}
    </div>
  );
}

function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/notification-settings");
      setSettings(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key, value) => {
    // Optimistic
    setSettings((s) => (s ? { ...s, [key]: value } : s));
    try {
      await api.patch("/admin/notification-settings", { [key]: value });
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      await api.post("/admin/notifications/test");
      toast.success("تم إرسال إشعار تجريبي — افحص تبويب السجل");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (!settings) return <div className="text-center py-12 text-primary animate-pulse">جارٍ التحميل…</div>;

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="rounded-xl border border-[#2196F3]/40 bg-[#2196F3]/10 text-[#5CB6F5] text-xs p-3 flex items-start gap-2">
        <Info size={14} weight="fill" className="mt-0.5 shrink-0" />
        <span>
          الإشعارات تعمل عبر النظام الحالي (DB + WhatsApp) لجميع الأدوار. زر الاختبار أدناه يُنشئ إشعاراً مباشراً في سجلك.
        </span>
      </div>

      {/* Test notification */}
      <button
        type="button"
        onClick={sendTest}
        disabled={sending}
        data-testid="notif-send-test"
        className="w-full rounded-xl border border-primary/30 bg-card hover:border-primary/60 p-4 flex items-center gap-3 text-right transition-all disabled:opacity-60"
      >
        <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
          <PaperPlaneTilt size={20} weight="duotone" className="text-primary" />
        </div>
        <div className="flex-1">
          <div className="text-white font-bold text-sm">إرسال إشعار تجريبي</div>
          <div className="text-white/60 text-xs mt-0.5">
            {sending ? "جارٍ الإرسال…" : "تحقّق من عمل الإشعارات على حسابك"}
          </div>
        </div>
      </button>

      {/* Two-column grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SectionCard title="تذكيرات السائق قبل الرحلة" icon={Alarm} testId="section-driver-reminders">
          <SettingRow label="قبل 12 ساعة" checked={settings.driver_reminder_12h} onChange={(v) => toggle("driver_reminder_12h", v)} testId="tgl-driver-12h" />
          <SettingRow label="قبل ساعتين" checked={settings.driver_reminder_2h} onChange={(v) => toggle("driver_reminder_2h", v)} testId="tgl-driver-2h" />
          <SettingRow label="قبل 30 دقيقة" checked={settings.driver_reminder_30m} onChange={(v) => toggle("driver_reminder_30m", v)} testId="tgl-driver-30m" />
          <SettingRow label="قبل 10 دقائق" checked={settings.driver_reminder_10m} onChange={(v) => toggle("driver_reminder_10m", v)} testId="tgl-driver-10m" />
        </SectionCard>

        <SectionCard title="إشعارات الإدارة" icon={UserIcon} testId="section-admin-notifs">
          <SettingRow label="حجز جديد من شركة" checked={settings.admin_new_booking} onChange={(v) => toggle("admin_new_booking", v)} testId="tgl-admin-booking" />
          <SettingRow label="تسجيل شركة جديدة" checked={settings.admin_new_company} onChange={(v) => toggle("admin_new_company", v)} testId="tgl-admin-company" />
          <SettingRow label="تسجيل سائق جديد" checked={settings.admin_new_driver} onChange={(v) => toggle("admin_new_driver", v)} testId="tgl-admin-driver" />
        </SectionCard>
      </div>

      <SectionCard title="إشعارات المستخدمين" icon={UsersThree} testId="section-user-notifs">
        <SettingRow label="تحديث حالة الحجز للشركة" checked={settings.user_booking_status} onChange={(v) => toggle("user_booking_status", v)} testId="tgl-user-status" />
        <SettingRow label="تعيين سائق لحجز" checked={settings.user_driver_assigned} onChange={(v) => toggle("user_driver_assigned", v)} testId="tgl-user-driver-assigned" />
      </SectionCard>
    </div>
  );
}

function LogTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(Array.isArray(data) ? data : (data?.notifications || []));
    } catch {
      toast.error("تعذر تحميل السجل");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-12 text-primary animate-pulse">جارٍ التحميل…</div>;
  if (items.length === 0) {
    return (
      <div className="text-center py-16 text-white/50 bg-card border border-primary/15 rounded-2xl">
        <Bell size={40} weight="duotone" className="text-primary/40 mx-auto mb-2" />
        لا توجد إشعارات
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="notif-log-list">
      {items.map((n) => {
        const unread = n.is_read === false;
        return (
          <div
            key={n.id || n._id}
            data-testid={`notif-item-${n.id}`}
            className="rounded-xl border border-primary/20 bg-card p-4"
          >
            <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
              <span className="inline-flex items-center gap-1 px-3 py-0.5 rounded-full bg-primary/15 border border-primary/50 text-primary text-[11px] font-bold">
                {typeLabel(n.type || n.category)}
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-white/60">{timeAgo(n.created_at)}</span>
                {unread && <span className="w-2 h-2 rounded-full bg-[#C62828]" title="غير مقروء" />}
              </div>
            </div>
            {(n.title || n.subject) && (
              <div className="text-primary font-bold text-sm mb-1">📌 {n.title || n.subject}</div>
            )}
            {(n.body || n.message) && (
              <div className="text-white/85 text-[13px] leading-relaxed whitespace-pre-line">
                {n.body || n.message}
              </div>
            )}
            {(n.trip_date || n.pickup_time) && (
              <div className="text-white/60 text-xs mt-2 num" dir="ltr">
                {fmtDate(n.pickup_time || n.trip_date)} · {fmtTime(n.pickup_time || n.trip_time)}
              </div>
            )}
            {n.recipient_name && (
              <div className="text-white/60 text-xs mt-2 flex items-center gap-1 pt-2 border-t border-primary/10">
                <UserIcon size={12} className="text-primary/60" />
                إلى: {n.recipient_name}
                {n.recipient_role && <span className="text-white/40">({n.recipient_role})</span>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function NotificationsPage() {
  const [tab, setTab] = useState("log");
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/admin/notifications").then(({ data }) => {
      const list = Array.isArray(data) ? data : (data?.notifications || []);
      setUnread(list.filter((n) => n.is_read === false).length);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-5xl mx-auto px-4 py-6" data-testid="notifications-page">
        <div className="mb-5">
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
            <Bell size={26} weight="duotone" className="text-primary" />
            الإشعارات
          </h1>
        </div>

        {/* Tabs */}
        <div
          className="flex flex-row-reverse items-center gap-2 mb-6"
          data-testid="notif-tabs"
        >
          <button
            type="button"
            onClick={() => setTab("settings")}
            data-testid="tab-settings"
            className={`inline-flex items-center flex-row-reverse gap-1.5 px-5 py-2.5 rounded-full border font-bold text-sm transition-all ${
              tab === "settings"
                ? "bg-primary border-primary text-primary-foreground"
                : "bg-transparent border-primary/30 text-primary hover:bg-primary/10"
            }`}
          >
            <Gear size={16} weight="duotone" /> الإعدادات
          </button>
          <button
            type="button"
            onClick={() => setTab("log")}
            data-testid="tab-log"
            className={`inline-flex items-center flex-row-reverse gap-1.5 px-5 py-2.5 rounded-full border font-bold text-sm transition-all ${
              tab === "log"
                ? "bg-primary border-primary text-primary-foreground"
                : "bg-transparent border-primary/30 text-primary hover:bg-primary/10"
            }`}
          >
            <ClockCountdown size={16} weight="duotone" />
            <span>
              السجل <span className="num">({fmtNum(unread)})</span>
            </span>
          </button>
        </div>

        {tab === "settings" ? <SettingsTab /> : <LogTab />}
      </main>
    </div>
  );
}
